import "./sessionSummaryPromptConsole.css";
import React, { useRef, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, Form } from "react-bootstrap";
import { logSession, LogLevel } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";
import IconInfo from "assets/Icon-Info.svg";
import { REASONING_LEVELS, DEFAULT_REASONING_LEVEL } from "constants/llmModels";
import { useAvailableModels } from "hooks/useAvailableModels";
import { LLMModelSelector } from "components/shared/LLMModelSelector/LLMModelSelector";
import { ReportInputsManifest } from "./ReportInputsManifest";
import {
  PromptVersionPicker,
  FILE_OPTION,
  UNKNOWN_OPTION,
  VERSIONS_STATUS,
  VERSIONS_LOADING,
} from "./PromptVersionPicker";
import { ParsedSummary } from "components/pages/Educator/Session/SessionSummaries/ParsedSummary";
import { getStudentList } from "components/pages/Educator/Session/SessionSummaries/ParsedSummary/studentExecutiveSummary";
import { getProp } from "components/pages/Educator/Session/SessionSummaries/ParsedSummary";
import { getQuestionList } from "components/pages/Educator/Session/SessionSummaries/ParsedSummary/questionBreakdown";
import {
  splitRunOutput,
  parseReportJson,
  hydrateParticipantNames,
  unrecognisedKeys,
  unknownPromptStage
} from "./consoleOutput";
import { useConsoleChallengeRows, CONSOLE_SESSION_INDEX } from "./useConsoleChallengeRows";
import { useConsoleTranscript } from "./useConsoleTranscript";
import { Transcript } from "components/pages/Educator/Session/SessionSummaries/Transcript";
import { ResponsiveTabNav, SESSION_REPORT_TABS } from "components/pages/Educator/Session/SessionSummaries/SessionReportTabs";
import { Summary, selectQuestionItems } from "components/pages/Educator/Session/SessionSummaries/Summary";
import { useReportDeterministic } from "components/pages/Educator/Session/SessionSummaries/useReportDeterministic";
import { QuestionSummary } from "components/pages/Educator/Session/SessionSummaries/QuestionSummary";
import { SessionScores } from "components/pages/Educator/Session/SessionSummaries/SessionScores";
import { isShortSession } from "utils/sessionDuration";

const POLL_INTERVAL_MS = 2000;
const PROMPT_VERSIONS_PATH = "session_summarization/v2/prompt_versions";
const OUTPUT_PANE_HEADING = "Output — the report this run produced, with the deterministic inputs behind it";
const DETERMINISTIC_INPUTS_HEADING = "Deterministic inputs used for this run";
// Stacked is what "Rendered" used to be called. With a third button "Rendered"
// would name two of the three choices — both Stacked and Teacher View are
// rendered — so it stopped distinguishing anything.
//
// Stacked stays the DEFAULT: judging a prompt means reading the whole thing at
// once, and making Teacher View the default would regress that loop.
const VIEW_STACKED = "Stacked";
const VIEW_TEACHER = "Teacher View";
const VIEW_RAW = "Raw";
const OUTPUT_VIEWS = [VIEW_STACKED, VIEW_TEACHER, VIEW_RAW];
const TARGET_PROMPT_LABEL = "merged_session_report_v15";
const CONSOLE_REPORT_LOCATION = "Admin Prompt Console";
// The four `ParsedSummary` drops for a session under three minutes, named as it
// heads them.
const SHORT_SESSION_HIDDEN_SECTIONS = [
  "Recommended Next Steps",
  "Top Collaboration Quotes",
  "Group Executive Summary",
  "Student Highlights",
];
// Tall enough to work in, short enough that the manifest and the output stay
// reachable. Past this the field scrolls rather than pushing the page down.
const FIELD_MAX_HEIGHT = "420px";
const SUB_REPORT_STYLE = { borderTop: "1px solid #e3ddd4", marginTop: "24px", paddingTop: "16px" };
const STUDENT_NAME_STYLE = { fontSize: "15px", fontWeight: "bold", margin: "0 0 4px" };
const SUB_REPORT_HEADING_STYLE = { color: "#6b665e", fontSize: "12px", letterSpacing: "0.04em", textTransform: "uppercase" };
const PAYLOAD_FIELD_HINT =
  "everything the model is given, not just the transcript";
const PREFILL_LOADING_MESSAGE = "Loading the deterministic inputs...";
const INVALID_SESSION_ID_MESSAGE = "That is not a valid session id.";
const EXPIRED_JOB_MESSAGE = "This job was not found or has expired.";
const DETERMINISTIC_FETCH_FAILED_MESSAGE =
  "Could not load the deterministic inputs for this session (BigQuery unavailable)."
  + " The affected sections are marked absent below.";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const LANGFUSE_SOURCE = "langfuse";
const FILE_SOURCE = "file";

// The version the prefill's text came from, or null when it did not come from one.
const prefillVersionOf = (prefill) =>
  prefill?.prompt_source === LANGFUSE_SOURCE && prefill.prompt_version
    ? { version: prefill.prompt_version, labels: prefill.prompt_labels || [] }
    : null;

// Submit resets the select to whatever the prefill's text came from.
// A prefill with no prompt_source comes from a server older than the version list.
const initialSelection = (prefill) => {
  if (prefill.prompt_source === undefined) return UNKNOWN_OPTION;
  return prefill.prompt_source === FILE_SOURCE ? FILE_OPTION : prefill.prompt_version ?? null;
};

const isWellFormedSessionId = (sessionId) => UUID_PATTERN.test((sessionId || "").trim());

/**
 * Each failure condition gets its own message. A generic error would leave the
 * admin unable to tell a broken prompt from a broken input.
 */
const prefillErrorMessage = (error, sessionId) => {
  const detail = error?.detail || error?.message || "";

  if (error?.status === 404) return `No compilation found for session ${sessionId}`;
  if (error?.status === 400) return detail || "Compilation not completed.";
  if (error?.status === 403) return detail || "Access denied. User is not a superuser";
  if (error?.status === 422) return INVALID_SESSION_ID_MESSAGE;
  return DETERMINISTIC_FETCH_FAILED_MESSAGE;
};

/**
 * A labelled field that can be folded away.
 *
 * The prompt and the payload are both long enough to bury everything below
 * them, and an admin comparing runs needs the manifest and the output on one
 * screen more often than they need to read either in full.
 */
const CollapsibleField = ({ label, hint, defaultOpen = true, onOpen, children }) => (
  <details
    open={defaultOpen}
    onToggle={(e) => e.target.open && onOpen?.()}
    style={{ borderTop: "1px solid #e3ddd4", paddingTop: "8px" }}
  >
    <summary style={{ cursor: "pointer", fontWeight: "bold" }}>
      {label}
      {hint && (
        <span style={{ color: colors.nightlyWoods500, fontWeight: "normal" }}>
          {" "}— {hint}
        </span>
      )}
    </summary>
    <div style={{ marginTop: "8px" }}>{children}</div>
  </details>
);

/**
 * The deterministic inputs, kept as the markdown the backend appended.
 *
 * Collapsed by default: they are the evidence behind the report, wanted when a
 * number looks wrong, not on every read.
 */
const DeterministicInputsBlock = ({ markdown }) => {
  if (!markdown) return null;

  return (
    <details style={{ borderTop: "1px solid #e3ddd4" }}>
      <summary style={{ cursor: "pointer", fontSize: "12px", padding: "8px 12px" }}>
        {DETERMINISTIC_INPUTS_HEADING}
      </summary>
      <div style={{
        borderTop: "1px solid #f0ebe3",
        fontFamily: "monospace",
        fontSize: "11px",
        lineHeight: 1.5,
        overflowX: "auto",
        padding: "12px",
        whiteSpace: "pre-wrap"
      }}>
        {markdown}
      </div>
    </details>
  );
};

/**
 * Top-level keys this renderer read nothing from.
 *
 * Prompts are published from a web UI instantly; the renderer needs a deploy.
 * Without this, a key the renderer has never heard of looks exactly like a
 * section the model chose not to write — which is how the v9 break reached a
 * real run unnoticed. Staff judging a prompt are the people who can act on it.
 */
const UnrecognisedKeys = ({ keys }) => {
  if (!keys || keys.length === 0) return null;

  return (
    <section style={SUB_REPORT_STYLE}>
      <h5 style={SUB_REPORT_HEADING_STYLE}>
        Unrecognised keys — nothing in this report read them
      </h5>
      {keys.map(({ key, value }) => (
        <div key={key} style={{ marginTop: "12px" }}>
          <code style={{ fontSize: "12px", fontWeight: "bold" }}>{key}</code>
          <pre style={{
            backgroundColor: "#f6f2ec",
            fontSize: "11px",
            marginTop: "4px",
            maxHeight: "200px",
            overflow: "auto",
            padding: "8px",
            whiteSpace: "pre-wrap"
          }}>
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      ))}
    </section>
  );
};

/**
 * What a teacher would NOT see for a session this short.
 *
 * Under three minutes the teacher's report drops four whole sections and leaves
 * only Session Headlines. The console renders them anyway — they are exactly
 * what an admin is iterating on, and short sessions are exactly the ones being
 * debugged — so it says so rather than letting the two surfaces differ in
 * silence, which is the failure this whole feature exists to remove.
 */
const ShortSessionNotice = ({ value }) => {
  if (!isShortSession(value)) return null;

  return (
    <Alert variant="warning" style={{ fontSize: "12px", marginBottom: "12px" }}>
      <strong>Short session.</strong> This session lasted under three minutes, so
      the teacher&apos;s report hides four sections of it:{" "}
      {SHORT_SESSION_HIDDEN_SECTIONS.join(", ")}. Only Session Headlines survives
      there. The console renders them all, because they are what a prompt is
      being judged on.
    </Alert>
  );
};

/**
 * One panel of the teacher's four, wired to the bar's own ids.
 *
 * `ResponsiveTabNav` points each tab at `session-tabpanel-<key>`, so the panels
 * carry those ids and label themselves from the tab that controls them.
 */
const TabPanel = ({ tabKey, active, children }) => (
  <div
    role="tabpanel"
    id={`session-tabpanel-${tabKey}`}
    aria-labelledby={`session-tab-${tabKey}`}
    hidden={!active}
    tabIndex={0}
    style={{ paddingTop: "12px" }}
  >
    {children}
  </div>
);

/**
 * The teacher's four tabs, over the run this console just produced.
 *
 * This is not a second renderer: the bar is the teacher's `ResponsiveTabNav`
 * and every panel mounts the teacher's own `Summary` or `Transcript`. What the
 * console owns is only WHICH of them goes in which panel — the teacher's own
 * container also owns the accordion, the stored-report fetch and the
 * "Insights Unavailable" and ongoing-session branches, none of which can apply
 * to a report that exists because it was just generated.
 */
const TeacherViewReport = ({
  hydrated,
  challengeRows,
  value,
  sessionRow,
  educatorId,
  transcript,
  isTranscriptLoading
}) => {
  const [tab, setTab] = useState("summary");
  const [questionIndex, setQuestionIndex] = useState(0);

  // Picking a question in the Session Summary lands on Question Detail with
  // that question selected, exactly as the teacher report does.
  const selectQuestion = (index) => {
    setQuestionIndex(index);
    setTab("questions");
  };

  // What every panel hands the teacher's Summary: the session row, the challenge
  // rows, the session's educator and the instruction to show the backend's
  // figures. Kept in one place so no panel can be given less than another.
  const teacherReportProps = {
    summaryData: hydrated,
    value,
    challengeData: challengeRows?.challengeData,
    educatorId,
    forceDeterministic: true,
    reconstructCollabLevels: true,
    // Where the person raising challenge feedback was reading the question.
    // What the card files is real: an admin's prompt-iteration note must not be
    // recorded as a teacher reporting a broken question.
    reportLocation: CONSOLE_REPORT_LOCATION,
  };

  // Only the open panel is built — an admin judging a prompt reads one section
  // at a time — but all four panels exist, because the bar's tabs point at them.
  const panelBody = {
    summary: () => <Summary {...teacherReportProps} onSelectQuestion={selectQuestion} />,
    students: () => <Summary {...teacherReportProps} studentsOnly />,
    questions: () => (
      <Summary
        {...teacherReportProps}
        index={CONSOLE_SESSION_INDEX}
        challengeDataErrors={challengeRows?.challengeDataErrors}
        setChallengeDataErrors={challengeRows?.setChallengeDataErrors}
        isChallengeDataLoading={challengeRows?.isChallengeDataLoading}
        fetchChallengeData={challengeRows?.fetchChallengeData}
        questionsOnly
        initialQuestionIndex={questionIndex}
        // The card renders the session's activity, so it can only be shown once
        // the console knows what that activity was. Until the prefill serves the
        // session row, every session would fall through to the calculator and
        // read "Try to make the number undefined".
        showQuestionCard={Boolean(value?.activity_type)}
      />
    ),
    transcript: () =>
      isTranscriptLoading ? (
        <div style={{ fontSize: "12px", padding: "12px" }}>Loading transcript...</div>
      ) : (
        <Transcript transcript={transcript} challengeData={challengeRows?.challengeData} />
      ),
  };

  return (
    <div>
      {/* The teacher's own score block, in the teacher's own place: above the
          tabs. Only this much of the collapsed session row is lifted — the rest
          of that header is session-LIST chrome, and the three fields it would
          need are parsed from the STORED report, so rendering them would put
          the previous run's numbers beside this run's.

          Shown only when the prefill served a row: everything in it — the code,
          the students, their levels — comes from that row, and a block with
          none of them would state "N/A" about a session nobody asked about. */}
      {sessionRow && <SessionScores value={sessionRow} educatorId={educatorId} />}

      <ResponsiveTabNav activeKey={tab} onSelect={setTab} />

      {SESSION_REPORT_TABS.map(({ key }) => (
        <TabPanel key={key} tabKey={key} active={tab === key}>
          {tab === key && panelBody[key]()}
        </TabPanel>
      ))}
    </div>
  );
};

/**
 * The report as a teacher would read it, or an explanation of why it cannot be.
 *
 * A model can return prose or a truncated object, so the failure is stated and
 * the raw text shown beneath it — rendering nothing would read as an empty
 * report rather than a bad run.
 */
const RenderedReport = ({
  modelText,
  participantNames,
  deterministic,
  challengeRows,
  value,
  sessionRow,
  educatorId,
  view,
  transcript,
  isTranscriptLoading
}) => {
  const { report, error } = parseReportJson(modelText);

  if (error) {
    return (
      <div style={{ padding: "12px" }}>
        <Alert variant="warning" style={{ fontSize: "12px", marginBottom: "12px" }}>
          <strong>Could not render this run.</strong> {error} The model&apos;s answer is
          shown below exactly as it came back.
        </Alert>
        <div style={{ fontFamily: "monospace", fontSize: "11px", whiteSpace: "pre-wrap" }}>
          {modelText}
        </div>
      </div>
    );
  }

  const hydrated = hydrateParticipantNames(report, participantNames);
  const students = getStudentList(hydrated) || [];
  const questions = getQuestionList(hydrated);
  const unread = unrecognisedKeys(report);
  const unknownStage = unknownPromptStage(report);

  // The teacher's report puts these behind tabs and pages them one at a time.
  // Here they are stacked: judging a prompt means reading the whole thing at
  // once, not clicking through five students to find the one it handled badly.
  return (
    <div style={{ padding: "12px" }}>
      {unknownStage && (
        <Alert variant="warning" style={{ fontSize: "12px", marginBottom: "12px" }}>
          <strong>Unknown prompt version {unknownStage}.</strong> This renderer was
          written against {TARGET_PROMPT_LABEL}. Everything it recognises is rendered
          below; anything it does not is listed at the end.
        </Alert>
      )}

      {/* Outside the view branch, like the unrecognised keys below: what a
          teacher would not see is a fact about the session, not about the
          layout the admin happens to be reading it in. */}
      <ShortSessionNotice value={value} />

      {view === VIEW_TEACHER ? (
        <TeacherViewReport
          hydrated={hydrated}
          challengeRows={challengeRows}
          value={value}
          sessionRow={sessionRow}
          educatorId={educatorId}
          transcript={transcript}
          isTranscriptLoading={isTranscriptLoading}
        />
      ) : (
        <>
          {/* The same grid Teacher View shows, un-paged: leaving the console's
              own imitation alive here would keep the drifted component in the
              tree, which is the whole thing this change removes. No
              onSelectQuestion — every question is already on the page below. */}
          <QuestionSummary
            challenges={selectQuestionItems({
              deterministic: deterministic?.enabled,
              challengeData: deterministic?.questionLevels,
              summaryData: hydrated,
            })}
          />
          <ParsedSummary summary={hydrated} deterministic={deterministic} />

          {students.length > 0 && (
            <section style={SUB_REPORT_STYLE}>
              <h5 style={SUB_REPORT_HEADING_STYLE}>Student Detail</h5>
              {students.map((student, index) => (
                <article key={`student-${index}`} style={{ marginTop: "16px" }}>
                  {/* The teacher's report renders the name outside ParsedSummary, in
                      its carousel header. Stacked, each block needs its own name or
                      the sections read as one undifferentiated run. */}
                  <h6 style={STUDENT_NAME_STYLE}>
                    {getProp(student, ["name", "student"]) || `Student ${index + 1}`}
                  </h6>
                  <ParsedSummary
                    summary={hydrated}
                    deterministic={deterministic}
                    studentsOnly
                    currentStudentIndex={index}
                  />
                </article>
              ))}
            </section>
          )}

          {questions.length > 0 && (
            <section style={SUB_REPORT_STYLE}>
              <h5 style={SUB_REPORT_HEADING_STYLE}>Question Detail</h5>
              {questions.map((_, index) => (
                <ParsedSummary
                  key={`question-${index}`}
                  summary={hydrated}
                  deterministic={deterministic}
                  questionsOnly
                  currentQuestionIndex={index}
                />
              ))}
            </section>
          )}
        </>
      )}

      {/* §2.7's anti-silent-failure device is the console's whole reason for
          existing; it must not become unreachable when previewing as a teacher. */}
      <UnrecognisedKeys keys={unread} />
    </div>
  );
};

/**
 * One pane, always. The model's answer is rendered as the report it describes,
 * with the deterministic inputs for that run beneath it. No stored report is
 * fetched and no comparison is drawn. Raw view stays one click away
 * so the exact bytes remain inspectable.
 */
const OutputPane = ({ output, participantNames, sessionRow, educatorId, sessionId }) => {
  const [view, setView] = useState(VIEW_STACKED);
  // What the teacher's components read. It is the served row wherever there is
  // one — no adaptation, it is their own `SessionDetails` — and otherwise the
  // one fact the console can always vouch for: WHICH session this is. That is
  // what keys the challenge rows, so the Group Results and the transcript
  // still read the backend's figures for a session whose row could not be
  // assembled. Nothing else is invented: with no `activity_type` there is no
  // question card, with no `ended_at` nothing is short, and the score block
  // waits on the row itself.
  const value = sessionRow || { session_id: sessionId };
  const challengeRows = useConsoleChallengeRows(sessionId, educatorId);
  // The figures the teacher's report shows beside the model's words, resolved by
  // the SAME builder the teacher's own report uses, so a prompt is judged
  // against what the teacher will actually read. Only three facts a teacher
  // never needs are stated here: who ran the session, that an admin is outside
  // the rollout gate, and that scores this session never recorded may be
  // reconstructed. Resolved here, above the view branch, so switching views
  // asks for nothing.
  const text = typeof output === "string" ? output : output?.summary;
  const { modelText, deterministicInputs } = splitRunOutput(text);
  // The run's own name -> id map, so the figures below key by the names THIS
  // run wrote. Parsed once here rather than inside the branch that renders it:
  // both views read the same deterministic prop.
  const participantIds = useMemo(
    () => parseReportJson(modelText).report?._participant_ids,
    [modelText],
  );
  const deterministic = useReportDeterministic({
    value,
    challengeData: challengeRows.challengeData,
    participantIds,
    forceDeterministic: true,
    educatorId,
    reconstructCollabLevels: true,
  });
  // The transcript is not model output: its own fetch, no dependency on the
  // report JSON, so it is unaffected by which prompt version produced the run.
  // Fetched with the run rather than on the switch to Teacher View, so toggling
  // views issues no request at all and the tab is ready when it is opened.
  const { transcript, isLoading: isTranscriptLoading } = useConsoleTranscript(sessionId);

  return (
    <div style={{ border: "1px solid #ccc4b8", borderRadius: "6px", marginTop: "24px" }}>
      <h4 style={{
        alignItems: "center",
        backgroundColor: "#f6f2ec",
        borderBottom: "1px solid #e3ddd4",
        display: "flex",
        fontSize: "12px",
        gap: "8px",
        justifyContent: "space-between",
        margin: 0,
        padding: "8px 12px"
      }}>
        <span>{OUTPUT_PANE_HEADING}</span>
        {text && (
          <div role="group" aria-label="Output view" style={{ display: "flex", gap: "4px" }}>
            {OUTPUT_VIEWS.map((option) => (
              <Button
                key={option}
                size="sm"
                variant={view === option ? "dark" : "outline-secondary"}
                aria-pressed={view === option}
                onClick={() => setView(option)}
                style={{ fontSize: "11px", padding: "2px 10px" }}
              >
                {option}
              </Button>
            ))}
          </div>
        )}
      </h4>

      {!text && (
        <div style={{
          color: "#948f88",
          fontSize: "12px",
          fontStyle: "italic",
          minHeight: "120px",
          padding: "12px"
        }}>
          Not generated yet.
        </div>
      )}

      {text && view === VIEW_RAW && (
        <div style={{
          fontFamily: "monospace",
          fontSize: "11px",
          lineHeight: 1.5,
          minHeight: "120px",
          padding: "12px",
          whiteSpace: "pre-wrap"
        }}>
          {text}
        </div>
      )}

      {text && view !== VIEW_RAW && (
        <>
          <RenderedReport
            modelText={modelText}
            participantNames={participantNames}
            deterministic={deterministic}
            challengeRows={challengeRows}
            value={value}
            sessionRow={sessionRow}
            educatorId={educatorId}
            view={view}
            transcript={transcript}
            isTranscriptLoading={isTranscriptLoading}
          />
          <DeterministicInputsBlock markdown={deterministicInputs} />
        </>
      )}
    </div>
  );
};

export const SessionSummaryPromptConsole = () => {
  const { authUserData, loading } = useAuthUserData();
  const history = useHistory();

  if (loading) return <></>;
  if (authUserData.isAnonymous) history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));

  return (
    <div className="session-summary-prompt-console" style={{
      display: "flex",
      backgroundColor: colors.milkGlass200,
      flexDirection: "column",
      gap: "24px"
    }}>
      {/* Info Alert */}
      <div style={{
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)"
      }}>
        <Alert style={{ marginBottom: 0 }} variant="">
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{
              alignItems: "center",
              display: "flex",
              minHeight: "36px",
              gap: "12px",
              flex: "1 0 0",
              paddingRight: "28px"
            }}>
              <img src={IconInfo} alt="Info" />
              {constants.SESSION_SUMMARY_PROMPT_CONSOLE_MESSAGE}
            </div>
          </div>
        </Alert>
      </div>

      {/* The console is its own page: no tab bar, no mode selector. */}
      <div style={{
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)",
        padding: "32px"
      }}>
        <ConsoleBody />
      </div>
    </div>
  );
};

const detectProviderFromModel = (model) => {
  if (!model) return "google";
  if (model.includes("claude")) return "anthropic";
  if (model.includes("gpt") || model.includes("o1") || model.includes("o3") || model.includes("o4")) return "openai";
  return "google";
};

const ConsoleBody = () => {
  const [formData, setFormData] = useState({});
  const [sessionFormData, setSessionFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [sessionSummaryData, setSessionSummaryData] = useState();
  const [newSessionSummary, setNewSessionSummary] = useState();
  const promptTextareaRef = useRef(null);
  const transcriptTextareaRef = useRef(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [promptVersions, setPromptVersions] = useState(VERSIONS_LOADING);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(null);
  const [versionLoadError, setVersionLoadError] = useState(null);
  // The text of the version last loaded into the textarea, by prefill or by a pick.
  const [loadedPrompt, setLoadedPrompt] = useState("");
  // A version picked since the last Submit has replaced the prefill's text.
  const [versionPicked, setVersionPicked] = useState(false);
  const latestPickRef = useRef(0);
  const {
    provider,
    setProvider,
    modelName,
    setModelName,
    modelOptions,
    modelsLoading,
  } = useAvailableModels();

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await request(`session_summarization/v2/generate_summary/status/${jobId}`, {
        method: "GET",
      });

      setJobStatus(response.status);

      if (response.status === "completed" && response.result) {
        setIsLoading(false);
        setNewSessionSummary(response.result);
        setJobStatus(null);
      } else if (response.status === "failed") {
        setIsLoading(false);
        setJobStatus(null);
        setError({ generation: response.error || "Summary generation failed" });
      } else {
        setTimeout(() => pollJobStatus(jobId), POLL_INTERVAL_MS);
      }
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      setJobStatus(null);
      logSession("Session summary job status poll failed", {
        level: LogLevel.WARNING,
        statusCode: e?.status,
        serverResponse: e?.detail,
        endpoint: "session_summarization/v2/generate_summary/status",
      });
      // The job record has expired, so there is nothing left to poll for. Stop
      // rather than looping forever.
      const expired = e?.status === 404;
      setError({
        generation: expired
          ? EXPIRED_JOB_MESSAGE
          : (e?.detail || e?.message || "Failed to check job status")
      });
    }
  }, []);

  // Grow to fit, but stop at FIELD_MAX_HEIGHT and scroll from there. Growing
  // without a ceiling is what left the prompt with no scrollbar at all.
  const growToFit = (ref) => {
    if (!ref.current) return;
    const textarea = ref.current;
    textarea.style.height = "auto";
    const capped = Math.min(textarea.scrollHeight, parseInt(FIELD_MAX_HEIGHT, 10));
    textarea.style.height = `${capped}px`;
  };

  const adjustPromptHeight = () => growToFit(promptTextareaRef);

  const adjustTranscriptHeight = () => growToFit(transcriptTextareaRef);

  const fetchPromptVersions = () => {
    setPromptVersions(VERSIONS_LOADING);
    request(PROMPT_VERSIONS_PATH, { method: "GET" })
      .then((response) => setPromptVersions({
        status: VERSIONS_STATUS.loaded,
        versions: response.versions || [],
        total: response.total_versions || 0,
      }))
      .catch((e) => setPromptVersions({
        ...VERSIONS_LOADING,
        status: VERSIONS_STATUS.failed,
        detail: e?.detail || e?.message,
      }));
  };

  // A later pick or Submit supersedes a pick still in flight; its answer is dropped.
  const supersedePendingPick = () => {
    latestPickRef.current += 1;
    setLoadingVersion(null);
  };

  const handlePickVersion = (version) => {
    const previousVersion = selectedVersion;
    latestPickRef.current += 1;
    const pickId = latestPickRef.current;
    const isCurrent = () => pickId === latestPickRef.current;
    setSelectedVersion(version);
    setLoadingVersion(version);
    setVersionLoadError(null);
    request(`${PROMPT_VERSIONS_PATH}/${version}`, { method: "GET" })
      .then((response) => {
        if (!isCurrent()) return;
        setFormData((prev) => ({ ...prev, prompt: response.prompt }));
        setLoadedPrompt(response.prompt);
        setVersionPicked(true);
        adjustPromptHeight();
      })
      .catch((e) => {
        if (!isCurrent()) return;
        setSelectedVersion(previousVersion);
        setVersionLoadError({ version, detail: e?.detail || e?.message });
      })
      .finally(() => {
        if (isCurrent()) setLoadingVersion(null);
      });
  };

  const handleGetSessionSummaryData = async (e) => {
    e.preventDefault();

    // A malformed id is refused here, before any request is attempted.
    if (!isWellFormedSessionId(sessionFormData.session_id)) {
      setSessionSummaryData(null);
      setError({ common: INVALID_SESSION_ID_MESSAGE });
      return;
    }

    setIsLoading(true);
    setError(null);
    setNewSessionSummary("");
    supersedePendingPick();
    fetchPromptVersions();
    await request(`session_summarization/v2/${sessionFormData.session_id}/summary`, {
      method: "GET",
    }).then((response) => {
      setIsLoading(false);
      const responseModel = response.llm || "gemini-2.5-flash-lite";
      setProvider(detectProviderFromModel(responseModel));
      setModelName(responseModel);
      setFormData(
        {
          prompt: response.prompt || "",
          temperature: response.temperature || 0,
          transcript: response.transcript || "",
          reasoning_effort: response.reasoning_effort || DEFAULT_REASONING_LEVEL,
        }
      );
      setSelectedVersion(initialSelection(response));
      setVersionLoadError(null);
      setVersionPicked(false);
      setLoadedPrompt(response.prompt || "");
      setSessionSummaryData(response);
      adjustPromptHeight();
      adjustTranscriptHeight();
    }).catch((e) => {
      console.log(e);
      setIsLoading(false);
      logSession("Session summary data fetch failed", {
        level: LogLevel.WARNING,
        statusCode: e?.status,
        serverResponse: e?.detail,
        endpoint: "session_summarization/v2/summary",
      });
      // A failed prefill leaves nothing loaded, so Generate is never offered.
      setSessionSummaryData(null);
      setError({ common: prefillErrorMessage(e, sessionFormData.session_id) });
    });
  };

  const handleChangeSessionIdForm = (e) => {
    const { name, value } = e.target;
    setSessionFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const alertStyle = {
    padding: "8px",
    marginBottom: 0,
    marginTop: "16px"
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setNewSessionSummary(null);
    setJobStatus("starting");

    try {
      const response = await request(`session_summarization/v2/generate_summary/async`, {
        payload: {
          session_id: sessionFormData.session_id,
          prompt: formData.prompt,
          llm: modelName,
          temperature: formData.temperature,
          transcript: formData.transcript,
          reasoning_effort: (provider === "anthropic" || formData.reasoning_effort === "none") ? null : formData.reasoning_effort,
        }
      });

      if (response.job_id) {
        setJobStatus("pending");
        pollJobStatus(response.job_id);
      }
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      setJobStatus(null);
      logSession("Session summary generation request failed", {
        level: LogLevel.WARNING,
        statusCode: e?.status,
        serverResponse: e?.detail,
        endpoint: "session_summarization/v2/generate_summary/async",
      });
      const errors = {};
      if (e.detail && Array.isArray(e.detail)) {
        e.detail.forEach((error) => {
          errors[error.loc.pop()] = error.msg;
        });
      } else {
        errors["generation"] = e?.detail || e?.message || "An unknown error occurred.";
      }
      setError(errors);
    }
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "24px",
      paddingTop: "24px"
    }}>
      <div>
        <Form onSubmit={handleGetSessionSummaryData}>
          <div style={{ display: "flex", flexDirection: "column" , gap: "32px" }}>
            <div style={{ display: "flex", flexDirection: "row" , gap: "32px", justifyContent: "space-between" }}>
              <div style={{ display: "flex", flexDirection: "column" , gap: "24px", width: "100%" }}>
                <Form.Group controlId="session_id">
                  <Form.Label style={{ fontWeight: "bold" }}>Session Id</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Enter session id"
                    value={sessionFormData.session_id || ""}
                    name="session_id"
                    onChange={handleChangeSessionIdForm}
                  />
                  {error?.session_id && (
                    <Alert style={alertStyle} variant="danger">{error.session_id}</Alert>
                  )}
                </Form.Group>
              </div>
              <Button style={{
                alignSelf: "end",
                backgroundColor: colors.nightlyWoods500,
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                height: "40px",
                padding: "0 16px",
                width: "100%" }}
                      disabled={isLoading}
                      type="submit">
                Submit
              </Button>
            </div>
            {error?.common && (
              <Alert style={alertStyle} variant="danger">{error.common}</Alert>
            )}
          </div>
        </Form>
      </div>
      {sessionSummaryData && <div>
        <ReportInputsManifest sections={sessionSummaryData.manifest?.sections} />
        <Form onSubmit={handleGenerate}>
          <div style={{ display: "flex", flexDirection: "column" , gap: "32px" }}>
            <div style={{ display: "flex", flexDirection: "column" , gap: "24px" }}>
              <PromptVersionPicker
                versionList={promptVersions}
                prefillVersion={prefillVersionOf(sessionSummaryData)}
                selectedVersion={selectedVersion}
                loadingVersion={loadingVersion}
                loadError={versionLoadError}
                edited={(formData.prompt || "") !== loadedPrompt}
                fromFile={sessionSummaryData.prompt_source === FILE_SOURCE}
                showFallbackNotice={sessionSummaryData.prompt_source === FILE_SOURCE && !versionPicked}
                versionUnknown={sessionSummaryData.prompt_source === undefined}
                fallbackReason={sessionSummaryData.prompt_fallback_reason}
                onPick={handlePickVersion}
              />
              <Form.Group controlId="prompt">
                <CollapsibleField label="Prompt" onOpen={adjustPromptHeight}>
                  <Form.Control
                    ref={promptTextareaRef}
                    required
                    aria-label="Prompt"
                    as="textarea"
                    placeholder="Enter new prompt"
                    value={formData.prompt || ""}
                    name="prompt"
                    onChange={handleChange}
                    style={{ maxHeight: FIELD_MAX_HEIGHT, overflowY: "auto" }}
                  />
                </CollapsibleField>
                {error?.prompt && (
                  <Alert style={alertStyle} variant="danger">{error.prompt}</Alert>
                )}
              </Form.Group>
              <LLMModelSelector
                provider={provider}
                onProviderChange={setProvider}
                modelName={modelName}
                onModelChange={setModelName}
                modelOptions={modelOptions}
                modelsLoading={modelsLoading}
                temperature={formData.temperature}
                onTemperatureChange={(val) => setFormData((prev) => ({ ...prev, temperature: val }))}
              />
              <Form.Group style={{ width: "33%" }}>
                <Form.Label style={{ fontWeight: "bold" }}>Reasoning Level</Form.Label>
                <Form.Select
                  required
                  value={provider === "anthropic" ? "none" : (formData.reasoning_effort || DEFAULT_REASONING_LEVEL)}
                  name="reasoning_effort"
                  onChange={handleChange}
                  disabled={provider === "anthropic"}
                >
                  {REASONING_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </Form.Select>
                {provider === "anthropic" && (
                  <Form.Text className="text-muted">Not supported for Anthropic models</Form.Text>
                )}
                {error?.reasoning_effort && (
                  <Alert style={alertStyle} variant="danger">{error.reasoning_effort}</Alert>
                )}
              </Form.Group>
              {error?.generation && (
                <Alert style={alertStyle} variant="danger">{error.generation}</Alert>
              )}
            </div>
            <Form.Group controlId="transcript">
              <CollapsibleField
                label="Session Inputs"
                hint={PAYLOAD_FIELD_HINT}
                defaultOpen={false}
                onOpen={adjustTranscriptHeight}
              >
                <Form.Control
                  ref={transcriptTextareaRef}
                  aria-label="Session Inputs"
                  as="textarea"
                  value={formData.transcript || ""}
                  name="transcript"
                  onChange={handleChange}
                  style={{ maxHeight: FIELD_MAX_HEIGHT, overflowY: "auto" }}
                />
              </CollapsibleField>
            </Form.Group>
            <Button style={{
              backgroundColor: colors.nightlyWoods500,
              border: "none",
              borderRadius: "6px",
              fontSize: "16px",
              height: "40px",
              padding: "0 16px" }}
                    disabled={isLoading}
                    type="submit">
              Generate new session summary
            </Button>
          </div>
        </Form>
      </div>}
      {isLoading && <div style={{ alignSelf: "center", position: "fixed", top: "50%", textAlign: "center" }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
        <div style={{ marginTop: "16px", fontSize: "16px", color: "#6c757d" }}>
          {!jobStatus && PREFILL_LOADING_MESSAGE}
          {jobStatus === "starting" && "Starting job..."}
          {jobStatus === "pending" && "Job queued..."}
          {jobStatus === "processing" && "Generating summary (this may take several minutes)..."}
        </div>
      </div>}
      {sessionSummaryData && (
        <OutputPane
          output={newSessionSummary}
          participantNames={sessionSummaryData.participant_names}
          // The session's own row, exactly as the prefill served it. No
          // adaptation: it is the teacher's `SessionDetails`, built by the same
          // helper their Session Reports row is built by. Null when the backend
          // could not assemble one, and the surface then shows only what does
          // not need it.
          sessionRow={sessionSummaryData.session || null}
          educatorId={sessionSummaryData.educator_id}
          sessionId={sessionSummaryData.session_id}
        />
      )}
    </div>
  );
};
